import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { therapistsApi } from '@/api/endpoints';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { NfseEligibilityBadge } from '@/components/shared/nfse-eligibility-badge';
import { ContactTherapistButton } from '@/components/shared/contact-therapist-button';
import { useRouteMeta, useStructuredData } from '@/lib/use-route-meta';
import { useMemo } from 'react';

export const Route = createFileRoute('/terapeutas/$id/')({ component: TherapistDetailPage });

const EDU_LEVEL_LABEL: Record<string, string> = {
  GRADUACAO: 'Graduação',
  POS_GRADUACAO: 'Pós-graduação',
  ESPECIALIZACAO: 'Especialização',
  MBA: 'MBA',
  MESTRADO: 'Mestrado',
  DOUTORADO: 'Doutorado',
  CURSO: 'Curso',
  EXTENSAO: 'Extensão',
  OUTRO: 'Outro',
};
const EDU_LEVEL_PRIORITY: Record<string, number> = {
  DOUTORADO: 0, MESTRADO: 1, MBA: 2, POS_GRADUACAO: 3, ESPECIALIZACAO: 4,
  GRADUACAO: 5, CURSO: 6, EXTENSAO: 7, OUTRO: 8,
};

const THERAPY_MODALITY_LABEL: Record<string, string> = {
  PSYCHOANALYST: 'Psicanalista',
  PSYCHOTHERAPIST: 'Psicoterapeuta (não-psicólogo)',
  INTEGRATIVE: 'Terapeuta Integrativo',
  HOLISTIC: 'Terapeuta Holístico',
  AROMATHERAPIST: 'Aromaterapeuta',
  FLORAL: 'Terapeuta Floral',
  FAMILY_CONSTELLATION: 'Constelação Familiar',
  HYPNOTHERAPIST: 'Hipnoterapeuta',
};

function TherapistDetailPage() {
  const { id } = Route.useParams();
  const isLoggedIn = !!tokenStore.getAccess();
  const { data, isLoading } = useQuery({
    queryKey: ['therapist', id],
    queryFn: () => therapistsApi.get(id),
  });

  const name = data?.user?.name;
  const role = data?.professionalCategory === 'PSYCHOLOGIST' ? 'Psicólogo(a)' : 'Terapeuta';
  const topSpecs = (data?.specialties ?? []).slice(0, 3).join(', ');
  const city = data?.city;
  const metaTitle = name
    ? `${name} — ${role}${city ? ` em ${city}` : ''}`
    : 'Perfil profissional';
  const metaDescription = name && data
    ? `${name} atende ${topSpecs || 'diversas demandas'}${city ? ` em ${city}` : ''}. ` +
      `Sessão de ${data.sessionDuration ?? 50}min · R$ ${Number(data.sessionPrice).toFixed(0)}. ` +
      (data.bio ? data.bio.slice(0, 90).replace(/\s+$/, '') + '…' : 'Agende online ou presencial.')
    : undefined;

  useRouteMeta({
    title: metaTitle,
    description: metaDescription,
    canonical: `https://nossoello.com.br/terapeutas/${id}`,
    image: data?.avatarUrl ?? data?.user?.image ?? undefined,
    type: 'profile',
  });

  const personLd = useMemo(() => {
    if (!data || !name) return null;
    const addr = city ? { '@type': 'PostalAddress', addressLocality: city, addressCountry: 'BR' } : undefined;
    const base: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': data.professionalCategory === 'PSYCHOLOGIST' ? ['Person', 'Physician'] : 'Person',
      name,
      jobTitle: role,
      image: data.avatarUrl ?? data.user?.image ?? undefined,
      url: `https://nossoello.com.br/terapeutas/${id}`,
      description: data.bio?.slice(0, 300) ?? undefined,
      knowsAbout: data.specialties?.length ? data.specialties : undefined,
      knowsLanguage: data.languages?.length ? data.languages : undefined,
      address: addr,
      offers: {
        '@type': 'Offer',
        price: Number(data.sessionPrice),
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
        category: 'Terapia',
      },
    };
    if (data.ratingCount > 0 && data.ratingAvg) {
      base.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Number(data.ratingAvg).toFixed(1),
        reviewCount: data.ratingCount,
        bestRating: 5,
        worstRating: 1,
      };
    }
    return base;
  }, [data, name, role, city, id]);

  useStructuredData(`therapist-${id}`, personLd);

  return (
    <div className="min-h-screen flex flex-col bg-[#FBF8F3]">
      <PublicHeader />
      <main className="flex-1 max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10 w-full">
        {isLoading ? (
          <div className="bg-white rounded-3xl p-8 h-96 animate-pulse" />
        ) : !data ? (
          <div className="bg-white rounded-3xl p-12 text-center">
            <p className="text-slate-500">Profissional não encontrado.</p>
            <Link to="/terapeutas" className="inline-block mt-4 text-teal-600 hover:underline">
              ← Voltar para a lista
            </Link>
          </div>
        ) : (
          <article className="bg-white rounded-3xl p-5 md:p-8 shadow-sm">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {data.avatarUrl || data.user?.image ? (
                <img
                  src={data.avatarUrl ?? data.user.image}
                  alt={data.user?.name}
                  className="size-32 rounded-3xl object-cover ring-4 ring-teal-100"
                />
              ) : (
                <div className="size-32 rounded-3xl bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold text-3xl">
                  {data.user?.name?.split(' ').map((p: string) => p[0]).slice(0, 2).join('').toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <h1 className="text-3xl font-extrabold text-slate-700">{data.user?.name}</h1>
                {/* Doc §4.3 — selo regulatório diferenciado por categoria */}
                {data.professionalCategory === 'PSYCHOLOGIST' && data.crpVerificationStatus === 'VERIFIED' && (
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                    <span aria-hidden>✓</span>
                    Psicólogo(a) · CRP {data.crpRegion}/{data.crpNumber?.split('/')[1] ?? data.crpNumber} · verificado
                  </div>
                )}
                {data.professionalCategory === 'THERAPIST' && data.therapyModality && (
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold px-2.5 py-1 rounded-full">
                    {THERAPY_MODALITY_LABEL[data.therapyModality] ?? data.therapyModality}
                  </div>
                )}
                {/* FB-3 — sub-especializações livres */}
                {data.specializations && data.specializations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {data.specializations.map((s: string) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100">{s}</span>
                    ))}
                  </div>
                )}
                {data.ratingCount > 0 && (
                  <div className="mt-2 flex items-center gap-1 text-sm text-slate-500">
                    <span className="text-amber-400 text-base">★</span>
                    <span className="font-semibold text-slate-700">
                      {Number(data.ratingAvg).toFixed(1)}
                    </span>
                    <span>({data.ratingCount} avaliações)</span>
                  </div>
                )}
                <p className="mt-1 text-sm uppercase tracking-wide text-teal-600 font-semibold">
                  {data.modality === 'HYBRID' ? 'Online ou Presencial' : data.modality === 'ONLINE' ? 'Online' : 'Presencial'}
                </p>
                {data.specialties?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {data.specialties.map((s: string) => (
                      <span key={s} className="text-xs px-3 py-1 rounded-full bg-coral-50 text-coral-700 font-medium">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="md:text-right shrink-0 w-full md:w-auto border-t md:border-t-0 border-slate-100 pt-5 md:pt-0">
                <div className="flex items-baseline md:block gap-2">
                  <p className="text-3xl md:text-3xl font-extrabold text-teal-600 md:mt-1 order-2 md:order-none">
                    R$ {Number(data.sessionPrice).toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-400 order-1 md:order-none">Sessão de {data.sessionDuration ?? 50}min</p>
                </div>
                <div className="mt-3 md:text-left md:max-w-xs">
                  <NfseEligibilityBadge professionalCategory={data.professionalCategory} size="sm" />
                </div>
                <div className="mt-4 flex flex-col items-stretch gap-2">
                  {isLoggedIn ? (
                    <Link
                      to="/terapeutas/$id/agendar"
                      params={{ id }}
                      className="inline-flex justify-center bg-coral-400 hover:bg-coral-500 text-white px-6 py-3 rounded-full font-medium shadow transition"
                    >
                      Agendar sessão
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      search={{ next: `/terapeutas/${id}` } as any}
                      className="inline-flex justify-center bg-coral-400 hover:bg-coral-500 text-white px-6 py-3 rounded-full font-medium shadow transition"
                    >
                      Entrar para agendar
                    </Link>
                  )}
                  <ContactTherapistButton therapistId={id} variant="profile" className="justify-center" />
                </div>
              </div>
            </div>

            {data.bio && (
              <section className="mt-10 pt-8 border-t border-slate-100">
                <h2 className="text-lg font-bold text-slate-700 mb-3">Sobre</h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">{data.bio}</p>
              </section>
            )}

            {data.approaches?.length > 0 && (
              <section className="mt-8">
                <h2 className="text-lg font-bold text-slate-700 mb-3">Abordagens</h2>
                <div className="flex flex-wrap gap-2">
                  {data.approaches.map((a: string) => (
                    <span key={a} className="text-sm px-3 py-1.5 rounded-full bg-teal-50 text-teal-700 font-medium">
                      {a}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {data.languages?.length > 0 && (
              <section className="mt-8">
                <h2 className="text-lg font-bold text-slate-700 mb-3">Idiomas atendidos</h2>
                <div className="flex flex-wrap gap-2">
                  {data.languages.map((l: string) => (
                    <span key={l} className="text-sm px-3 py-1.5 rounded-full bg-cream-100 text-slate-700 font-medium border border-cream-200">
                      {l}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {data.education?.length > 0 && (
              <section className="mt-8">
                <h2 className="text-lg font-bold text-slate-700 mb-3">Formação</h2>
                <ul className="space-y-2">
                  {[...data.education]
                    // FB-4 — cronologia da carreira (mais antigo primeiro).
                    // Título sem ano vai pro final. Empate resolvido pela
                    // prioridade do nível (Doutorado antes de Curso).
                    .sort((a: any, b: any) => {
                      const ya = a.year ?? Infinity;
                      const yb = b.year ?? Infinity;
                      if (ya !== yb) return ya - yb;
                      const pa = EDU_LEVEL_PRIORITY[a.level] ?? 99;
                      const pb = EDU_LEVEL_PRIORITY[b.level] ?? 99;
                      return pa - pb;
                    })
                    .map((e: any) => (
                      <li key={e.id} className="text-sm text-slate-600 flex items-start gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded shrink-0 mt-0.5">
                          {EDU_LEVEL_LABEL[e.level] ?? e.level}
                        </span>
                        <span>
                          <span className="font-medium">{e.course}</span> · {e.institution}
                          {e.year && <span className="text-slate-400"> · {e.year}</span>}
                        </span>
                      </li>
                    ))}
                </ul>
              </section>
            )}

            {/* FB-2 — presença online: vídeo + site + redes sociais */}
            {(data.videoUrl || data.websiteUrl || data.linkedinUrl || data.instagramUrl || data.twitterUrl || data.youtubeUrl) && (
              <section className="mt-8">
                <h2 className="text-lg font-bold text-slate-700 mb-3">Conheça o profissional</h2>
                <div className="flex flex-wrap gap-2">
                  {data.videoUrl && <PublicLinkChip href={data.videoUrl} label="🎬 Vídeo de apresentação" />}
                  {data.websiteUrl && <PublicLinkChip href={data.websiteUrl} label="🌐 Site" />}
                  {data.linkedinUrl && <PublicLinkChip href={data.linkedinUrl} label="LinkedIn" />}
                  {data.instagramUrl && <PublicLinkChip href={data.instagramUrl} label="Instagram" />}
                  {data.twitterUrl && <PublicLinkChip href={data.twitterUrl} label="X / Twitter" />}
                  {data.youtubeUrl && <PublicLinkChip href={data.youtubeUrl} label="YouTube" />}
                </div>
              </section>
            )}

            {/* Doc §6 — aviso de transparência obrigatório p/ categoria B */}
            {data.professionalCategory === 'THERAPIST' && (
              <aside className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 leading-relaxed">
                <strong>Aviso de transparência.</strong> Esta modalidade não é uma profissão
                regulamentada no Brasil e não substitui acompanhamento médico ou psicológico.
                As informações de formação são fornecidas pelo próprio profissional.
              </aside>
            )}
          </article>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

function PublicLinkChip({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:text-teal-700 hover:bg-teal-50 transition font-medium"
    >
      {label}
      <span aria-hidden className="text-slate-400 text-xs">↗</span>
    </a>
  );
}
